import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Instagram, Facebook, Calendar, Trophy, ArrowRight, Youtube, Users, MapPin } from 'lucide-react';
import { horses, news, gallery, biography, events, socialMedia } from '../data/mock';
import { formatRange } from '../utils/calendar';
import { useLanguage } from '../context/LanguageContext';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Home = () => {
  const [currentHorse, setCurrentHorse] = useState(0);
  const { t, language } = useLanguage();

  // Auto-rotate horses
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHorse((prev) => (prev + 1) % Math.min(horses.length, 6));
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section - Full Screen */}
      <section className="relative h-screen">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bassi-riding/artifacts/3j6ha38y_FILE_9HW_1769.jpeg)',
            filter: 'grayscale(100%)'
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-8 md:px-16">
            <div className="max-w-2xl">
              {/* Decorative Line - Italian Flag */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-1 flex">
                  <div className="w-1/3 h-full bg-green-500"></div>
                  <div className="w-1/3 h-full bg-white"></div>
                  <div className="w-1/3 h-full bg-red-500"></div>
                </div>
                <span className="text-cyan-400 tracking-[0.3em] text-sm font-medium">{t('internationalRider')}</span>
              </div>
              
              {/* Main Title */}
              <h1 className="text-6xl md:text-8xl font-black mb-2 leading-none">
                <span className="text-white">GIACOMO</span>
              </h1>
              <h1 className="text-6xl md:text-8xl font-black mb-6 leading-none">
                <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-cyan-400 bg-clip-text text-transparent">BASSI</span>
              </h1>
              
              {/* Quote */}
              <p className="text-xl md:text-2xl font-light italic text-zinc-300 mb-8">
                «{t('quote').split('100% improvement')[0]}<span className="text-pink-400 font-medium">{t('improvement')}</span>»
              </p>
              
              {/* FEI Ranking Badge - Moved here */}
              <div className="inline-block bg-black/80 backdrop-blur-sm border border-zinc-700 p-4 md:p-6">
                <p className="text-zinc-400 text-xs md:text-sm tracking-wider mb-1">{t('feiRanking')}</p>
                <p className="text-4xl md:text-5xl font-black text-cyan-400">{biography.worldRanking.position}</p>
                <p className="text-zinc-500 text-xs md:text-sm">{t('worldRanking')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links with Follower Count */}
        <div className="absolute bottom-8 left-8 md:left-16 z-20">
          {/* Total Followers Counter */}
          <div className="flex items-center gap-2 mb-4 bg-black/60 backdrop-blur-sm px-4 py-2 border border-zinc-700">
            <Users size={18} className="text-pink-500" />
            <span className="text-2xl font-black bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              {socialMedia.totalFollowers.toLocaleString()}+
            </span>
            <span className="text-zinc-400 text-sm">{t('followers')}</span>
          </div>
          
          {/* Social Icons */}
          <div className="flex gap-3">
            <a
              href={socialMedia.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 border border-zinc-600 flex items-center justify-center hover:border-pink-500 hover:text-pink-500 transition-colors bg-black/40"
            >
              <Instagram size={18} />
            </a>
            <a
              href={socialMedia.tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 border border-zinc-600 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 transition-colors bg-black/40"
            >
              <TikTokIcon className="w-[18px] h-[18px]" />
            </a>
            <a
              href={socialMedia.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 border border-zinc-600 flex items-center justify-center hover:border-blue-500 hover:text-blue-500 transition-colors bg-black/40"
            >
              <Facebook size={18} />
            </a>
            <a
              href={socialMedia.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 border border-zinc-600 flex items-center justify-center hover:border-red-500 hover:text-red-500 transition-colors bg-black/40"
            >
              <Youtube size={18} />
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-zinc-500 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-cyan-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-8 md:px-16">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-black">
                {t('lastNews').split(' ')[0]} <span className="text-pink-500">{t('lastNews').split(' ').slice(1).join(' ') || 'news'}</span>
              </h2>
            </div>
            <Link 
              to="/news"
              data-testid="view-all-news"
              className="text-cyan-400 hover:text-cyan-300 transition-colors tracking-wider font-medium hidden md:flex items-center gap-2"
            >
              {t('seeAll')} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {news.slice(0, 2).map((item, index) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                data-testid={`news-card-${item.id}`}
                className="group relative overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-pink-400 text-sm mb-2">{item.date}</p>
                  <h3 className="text-2xl font-bold group-hover:text-cyan-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-zinc-400 mt-2 line-clamp-2">{item.excerpt}</p>
                </div>
                {/* Accent Line */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-pink-500 to-cyan-400 group-hover:w-full transition-all duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Events & Results Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-8 md:px-16">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Next Events */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Calendar className="text-cyan-400" size={24} />
                <h2 className="text-3xl font-black">{t('nextEvents').split(' ')[0]} <span className="text-cyan-400">{t('nextEvents').split(' ').slice(1).join(' ') || 'events'}</span></h2>
              </div>
              <div className="space-y-3 md:max-h-[640px] md:overflow-y-auto md:pr-2" data-testid="upcoming-events-list">
                {events.upcoming.map((event) => (
                  <div key={event.id} className="border-l-4 border-cyan-400 pl-6 py-3 bg-zinc-900/50 hover:bg-zinc-900 transition-colors">
                    <h3 className="font-bold text-lg">{event.name}</h3>
                    <p className="text-zinc-400 text-sm">{event.location}</p>
                    <p className="text-cyan-400 text-sm mt-1">{formatRange(event.startDate, event.endDate, language)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Last Results */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Trophy className="text-pink-500" size={24} />
                <h2 className="text-3xl font-black">{t('lastResults').split(' ')[0]} <span className="text-pink-500">{t('lastResults').split(' ').slice(1).join(' ') || 'results'}</span></h2>
              </div>
              <div className="space-y-4">
                {events.past.slice(0, 6).map((event) => (
                  <div key={event.id} className="border-l-4 border-pink-500 pl-6 py-3 bg-zinc-900/50 hover:bg-zinc-900 transition-colors">
                    <h3 className="font-bold text-lg">{event.name}</h3>
                    <p className="text-zinc-400 text-sm">{event.location}</p>
                    <p className="text-pink-400 text-sm mt-1 font-medium">{event.result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/calendar"
              data-testid="view-full-calendar"
              className="inline-flex items-center gap-2 border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black px-8 py-3 tracking-wider font-medium transition-colors"
            >
              {t('calendarTitle')} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Competition Horses Section */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-8 md:px-16">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl md:text-5xl font-black">
              {t('competitionHorses').split(' ')[0]} <span className="text-cyan-400">{t('competitionHorses').split(' ').slice(1).join(' ') || 'Horses'}</span>
            </h2>
            <Link 
              to="/horses"
              data-testid="view-all-horses"
              className="text-pink-500 hover:text-pink-400 transition-colors tracking-wider font-medium hidden md:flex items-center gap-2"
            >
              {t('seeAll')} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Horses Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {horses.slice(0, 8).map((horse, index) => (
              <Link
                key={horse.id}
                to={`/horses/${horse.id}`}
                data-testid={`horse-card-${horse.id}`}
                className="group relative overflow-hidden aspect-square"
              >
                {/* Horse Name on Top */}
                <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
                  <h3 className="font-black text-lg leading-tight text-white">{horse.name}</h3>
                  {horse.feiId && (
                    <p className="text-xs text-cyan-400">FEI: {horse.feiId}</p>
                  )}
                </div>
                <img
                  src={horse.image}
                  alt={horse.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-pink-500/80 to-cyan-400/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white font-bold tracking-wider">{t('viewProfile').toUpperCase()}</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile View All */}
          <div className="mt-8 text-center md:hidden">
            <Link 
              to="/horses"
              className="inline-flex items-center gap-2 text-pink-500 font-medium"
            >
              {t('seeAll')} {t('horses').toLowerCase()} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-8 md:px-16">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl md:text-5xl font-black">
              {t('photoGallery').split(' ')[0]} <span className="text-pink-500">{t('photoGallery').split(' ').slice(1).join(' ') || 'Gallery'}</span>
            </h2>
            <Link 
              to="/photos"
              data-testid="view-all-photos"
              className="text-cyan-400 hover:text-cyan-300 transition-colors tracking-wider font-medium hidden md:flex items-center gap-2"
            >
              {t('seeAll')} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.slice(0, 8).map((photo) => (
              <Link
                key={photo.id}
                to="/photos"
                className="group aspect-square overflow-hidden relative"
              >
                <img
                  src={photo.image}
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium tracking-wider">
                    VIEW
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-16 bg-zinc-950 border-t border-zinc-800">
        <div className="container mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-zinc-400">{t('partnersAndSponsors')}</h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 hover:opacity-100 transition-opacity">
            <span className="text-2xl font-bold tracking-wider text-zinc-500">VEREDUS</span>
            <span className="text-2xl font-bold tracking-wider text-zinc-500">PRESTIGE</span>
            <span className="text-2xl font-bold tracking-wider text-zinc-500">OPHENA</span>
            <span className="text-2xl font-bold tracking-wider text-zinc-500">GARMIN</span>
          </div>
          <div className="text-center mt-8">
            <Link 
              to="/sponsors" 
              className="text-cyan-400 hover:text-cyan-300 text-sm tracking-wider"
            >
              {t('viewAllSponsors')}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-900/30 via-black to-cyan-900/30">
        <div className="container mx-auto px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            {t('followTheJourney').split(' ').slice(0, -1).join(' ')} <span className="text-pink-500">{t('followTheJourney').split(' ').slice(-1)}</span>
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            {t('followJourneyDesc')}
          </p>
          <a
            href={`https://instagram.com/${biography.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-cta"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-pink-500 to-pink-600 px-8 py-4 font-bold tracking-wider hover:from-pink-600 hover:to-pink-700 transition-all"
          >
            <Instagram size={24} />
            {t('follow')} @{biography.instagram.toUpperCase()}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
